import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import { isArray, isBoolean, isObject, isString } from "lodash";
import {
  availableDateFormatsList,
  parseDatePickerValue,
} from "@/src/shared/utils/parseDatePickerValue";
import { maskFunctions } from "@/src/shared/utils/mask";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import dayjs from "@/src/shared/utils/dayjs";

const safeParce = (value: string): any => {
  if (!value && typeof value !== "boolean" && typeof value !== "number")
    return "";
  try {
    const parsedData = JSON.parse(value);
    return parsedData;
  } catch (e) {
    return value;
  }
};
export const parseVariableToString = (val: any): string => {
  const value = safeParce(val);
  if (isArray(value) && isObject(value[0])) {
    return value.map((item: any) => item.label || item.value).join(", ");
  }
  if (isArray(value) && !isObject(value[0])) {
    return value.join(", ");
  }
  if (isObject(value)) {
    // @ts-ignore
    return value.label || value.value || "";
  }
  if (isBoolean(value)) {
    return value ? "Так" : "Ні";
  }

  if (isString(value)) {
    const dateCandidate = parseDatePickerValue(value, availableDateFormatsList);

    if (
      dateCandidate?.isValid() &&
      dayjs(value, availableDateFormatsList, true).isValid()
    ) {
      return dateCandidate.format("DD.MM.YYYY");
    }

    return value;
  }

  return value ?? "";
};

const regex = /{{(.*?)}}/g;

export const extractNames = (inputString: string): string[] => {
  const matches: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(inputString)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};

export const replaceNames = (
  inputString: string,
  replacements: Record<string, string>,
  withoutDefault = false,
  makeBold = false,
): React.ReactNode => {
  return typeof inputString === "string"
    ? inputString.split(regex).map((segment, index) => {
        if (index % 2 === 0) return segment;
        const replacement =
          replacements[segment] || (withoutDefault ? "" : segment);
        return makeBold ? (
          <span className="font-semibold" key={index}>
            {replacement}
          </span>
        ) : (
          replacement
        );
      })
    : inputString;
};

export const ParsedValue = ({
  value,
  makeBold = false,
  defaultValues,
  maskType,
}: {
  value: string;
  makeBold?: boolean;
  defaultValues?: Record<string, string>;
  maskType?: string;
}) => {
  const { watch } = useFormContext();
  const contextFields = extractNames(value);
  const formValues = watch(contextFields.map((n) => n.trim()));
  const parsedValue = useMemo(() => {
    const values =
      defaultValues ||
      Object.fromEntries(
        contextFields.map((name, i) => {
          const label = parseVariableToString(formValues[i]);
          if (maskType === "thousandSeparator") {
            return [name, maskFunctions.thousandSeparator(label)];
          }
          return [name, label];
        }),
      );
    return replaceNames(value, values, true, makeBold);
  }, [contextFields, formValues, value, makeBold]);

  return <>{parsedValue}</>;
};

export const TextWithValue = ({
  text = "",
  valueString = "",
  textFontSize,
  valueFontSize,
  maskType,
  color,
  className,
}: {
  text?: string;
  valueString?: string;
  textFontSize?: number;
  valueFontSize?: number;
  maskType?: string;
  color?: string;
  className?: string;
}) => {
  return (
    <div
      className={cn("flex w-full items-center gap-2", className)}
      style={{ color }}
    >
      <div style={{ fontSize: textFontSize }}>{text}</div>
      <div className="font-semibold" style={{ fontSize: valueFontSize }}>
        <ParsedValue value={valueString} maskType={maskType} />
      </div>
    </div>
  );
};
